// Notification Service - Handles desktop notifications and sounds

// Generate a simple notification sound using Web Audio API
const generateNotificationSound = (type: 'default' | 'chime' | 'ding' | 'pop' | 'bell' = 'default', volume?: number): void => {
  try {
    const vol = volume !== undefined ? volume : notificationSettings.volume;
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const masterGain = audioContext.createGain();
    masterGain.gain.setValueAtTime(vol, audioContext.currentTime);
    masterGain.connect(audioContext.destination);
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(masterGain);
    
    // Different sound patterns based on type
    switch (type) {
      case 'chime':
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(1200, audioContext.currentTime + 0.1);
        oscillator.frequency.exponentialRampToValueAtTime(800, audioContext.currentTime + 0.2);
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.3);
        break;
        
      case 'ding':
        oscillator.frequency.setValueAtTime(1000, audioContext.currentTime);
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
        break;
        
      case 'pop':
        oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(200, audioContext.currentTime + 0.1);
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.5, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.15);
        break;
        
      case 'bell':
        oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.8);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.8);
        
        // Add second tone for bell effect
        const osc2 = audioContext.createOscillator();
        const gain2 = audioContext.createGain();
        osc2.connect(gain2);
        gain2.connect(masterGain);
        osc2.frequency.setValueAtTime(1320, audioContext.currentTime);
        osc2.type = 'sine';
        gain2.gain.setValueAtTime(0.2, audioContext.currentTime);
        gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.6);
        osc2.start(audioContext.currentTime);
        osc2.stop(audioContext.currentTime + 0.6);
        break;
        
      default: // default
        oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
        oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1); // E5
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.3);
    }
  } catch (error) {
    console.error('Failed to play notification sound:', error);
  }
};

// Store settings locally
let notificationSettings = {
  enabled: true,
  soundEnabled: true,
  soundType: 'default' as 'default' | 'chime' | 'ding' | 'pop' | 'bell' | 'none',
  volume: 0.5,
  content: 'full' as 'full' | 'minimal' | 'count',
};

// Update settings
export const updateNotificationSettings = (settings: Partial<typeof notificationSettings>) => {
  notificationSettings = { ...notificationSettings, ...settings };
  // Store in localStorage
  localStorage.setItem('notificationSettings', JSON.stringify(notificationSettings));
};

// Load settings from localStorage
export const loadNotificationSettings = () => {
  try {
    const stored = localStorage.getItem('notificationSettings');
    if (stored) {
      notificationSettings = { ...notificationSettings, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.error('Failed to load notification settings:', error);
  }
};

// Play notification sound
export const playNotificationSound = (type?: 'default' | 'chime' | 'ding' | 'pop' | 'bell' | 'none') => {
  const soundType = type || notificationSettings.soundType;
  if (soundType === 'none' || !notificationSettings.soundEnabled) {
    return;
  }
  generateNotificationSound(soundType);
};

// Request notification permission
export const requestNotificationPermission = async (): Promise<boolean> => {
  if (!('Notification' in window)) {
    console.warn('[DesktopNotif] This browser does not support desktop notifications');
    return false;
  }

  console.log('[DesktopNotif] Current permission:', Notification.permission);

  if (Notification.permission === 'granted') {
    console.log('[DesktopNotif] Permission already granted');
    return true;
  }

  if (Notification.permission !== 'denied') {
    console.log('[DesktopNotif] Requesting permission...');
    const permission = await Notification.requestPermission();
    console.log('[DesktopNotif] Permission result:', permission);
    return permission === 'granted';
  }

  console.warn('[DesktopNotif] Permission was denied previously');
  return false;
};

// Show desktop notification
export const showDesktopNotification = async (options: {
  title: string;
  body: string;
  icon?: string;
  tag?: string;
  data?: any;
  onClick?: () => void;
}) => {
  if (!notificationSettings.enabled) {
    return;
  }

  // Check if we have permission
  if (Notification.permission !== 'granted') {
    console.warn('[DesktopNotif] No permission, trying to request...');
    const granted = await requestNotificationPermission();
    if (!granted) {
      console.warn('[DesktopNotif] Permission not granted, cannot show notification');
      return;
    }
  }

  try {
    console.log('[DesktopNotif] Creating notification:', options.title, options.body);
    const notification = new Notification(options.title, {
      body: options.body,
      icon: options.icon || '/favicon.ico',
      tag: options.tag,
      data: options.data,
      requireInteraction: true,
    });

    // Play sound
    if (notificationSettings.soundEnabled && notificationSettings.soundType !== 'none') {
      playNotificationSound();
    }

    // Handle click
    if (options.onClick) {
      notification.onclick = () => {
        window.focus();
        options.onClick!();
        notification.close();
      };
    }

    // Auto close after 15 seconds
    setTimeout(() => {
      notification.close();
    }, 15000);

    return notification;
  } catch (error) {
    console.error('Failed to show notification:', error);
  }
};

// Show new email notification
export const showNewEmailNotification = async (email: {
  from: string;
  subject: string;
  preview?: string;
  id?: string;
}) => {
  let title = 'New Email';
  let body = '';

  switch (notificationSettings.content) {
    case 'full':
      title = `New email from ${email.from}`;
      body = `${email.subject}${email.preview ? '\n' + email.preview.substring(0, 100) : ''}`;
      break;
    case 'minimal':
      title = `New email from ${email.from}`;
      body = email.subject;
      break;
    case 'count':
      title = 'New Email';
      body = 'You have a new email';
      break;
  }

  return showDesktopNotification({
    title,
    body,
    tag: email.id || 'new-email',
    onClick: () => {
      // Navigate to email if possible
      if (email.id) {
        window.location.href = `/mail/inbox/${email.id}`;
      }
    },
  });
};

// Test notification
export const testNotification = async () => {
  // First request permission
  const hasPermission = await requestNotificationPermission();
  
  if (!hasPermission) {
    alert('Please allow notifications in your browser settings');
    return false;
  }

  // Show test notification
  await showDesktopNotification({
    title: 'Test Notification',
    body: 'Desktop notifications are working correctly!',
    tag: 'test-notification',
  });

  return true;
};

// Test sound only
export const testSound = (type: 'default' | 'chime' | 'ding' | 'pop' | 'bell' = 'default') => {
  generateNotificationSound(type);
};

// Initialize notification service
export const initNotificationService = () => {
  loadNotificationSettings();
  
  // Request permission on init if enabled
  if (notificationSettings.enabled && Notification.permission === 'default') {
    requestNotificationPermission();
  }
};

// Sync settings from backend API (called after settingsStore fetches)
export const syncFromBackendSettings = (backendSettings: {
  enableDesktopNotifications?: boolean;
  soundEnabled?: boolean;
  notificationSound?: string;
  soundVolume?: number;
  notificationContent?: string;
}) => {
  const updates: Partial<typeof notificationSettings> = {};
  if (backendSettings.enableDesktopNotifications !== undefined) {
    updates.enabled = backendSettings.enableDesktopNotifications;
  }
  if (backendSettings.soundEnabled !== undefined) {
    updates.soundEnabled = backendSettings.soundEnabled;
  }
  if (backendSettings.notificationSound !== undefined) {
    updates.soundType = backendSettings.notificationSound as any;
  }
  if (backendSettings.soundVolume !== undefined) {
    updates.volume = backendSettings.soundVolume / 100; // Convert 0-100 to 0-1
  }
  if (backendSettings.notificationContent !== undefined) {
    updates.content = backendSettings.notificationContent as any;
  }
  if (Object.keys(updates).length > 0) {
    updateNotificationSettings(updates);
  }
};

export default {
  playNotificationSound,
  requestNotificationPermission,
  showDesktopNotification,
  showNewEmailNotification,
  testNotification,
  testSound,
  updateNotificationSettings,
  initNotificationService,
  syncFromBackendSettings,
};
